import React, { useEffect, useState } from "react";
import Footer from "./Footer";
import { useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../features/user/userSlice";
import { Link, useNavigate } from "react-router-dom";

const loginSchema = yup.object({
  email: yup
    .string()
    .required("Email is Required")
    .email("Email must be valid"),
  password: yup.string().required("Password is Required"),
});

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

  @keyframes lgFadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes lgOrb {
    0%,100% { transform: translate(0,0) scale(1); }
    33%      { transform: translate(30px,-20px) scale(1.1); }
    66%      { transform: translate(-20px,15px) scale(0.95); }
  }

  .lg-page {
    font-family: 'Inter', sans-serif;
    min-height: 100vh;
    background: linear-gradient(135deg, #0f172a 0%, #134e4a 50%, #0f172a 100%);
    display: flex;
    flex-direction: column;
    position: relative;
    overflow: hidden;
  }

  /* Background orbs */
  .lg-orb {
    position: absolute;
    border-radius: 50%;
    pointer-events: none;
    filter: blur(80px);
    opacity: 0.35;
    animation: lgOrb 12s ease-in-out infinite;
  }
  .lg-orb--1 { width: 400px; height: 400px; background: #14b8a6; top: -100px; left: -100px; }
  .lg-orb--2 { width: 300px; height: 300px; background: #0ea5e9; bottom: -80px; right: -80px; animation-duration: 9s; animation-delay: -4s; }

  /* Center container */
  .lg-center {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 16px 100px;
  }

  .lg-card {
    width: 100%;
    max-width: 440px;
    background: rgba(255,255,255,0.97);
    border-radius: 28px;
    box-shadow: 0 40px 120px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.12);
    overflow: hidden;
    animation: lgFadeUp 0.5s cubic-bezier(0.34,1.1,0.64,1);
  }

  /* Card header */
  .lg-card-head {
    background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
    padding: 36px 32px 32px;
    text-align: center;
    position: relative;
  }
  .lg-card-head::after {
    content: '';
    position: absolute;
    bottom: -1px; left: 0; right: 0;
    height: 30px;
    background: rgba(255,255,255,0.97);
    border-radius: 28px 28px 0 0;
  }
  .lg-logo-ring {
    width: 64px; height: 64px;
    border-radius: 50%;
    background: rgba(255,255,255,0.2);
    border: 2px solid rgba(255,255,255,0.4);
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto 14px;
    font-size: 28px;
    backdrop-filter: blur(8px);
  }
  .lg-card-title {
    font-size: 26px; font-weight: 900; color: #fff;
    letter-spacing: -0.04em; margin: 0 0 6px;
  }
  .lg-card-sub {
    font-size: 13px; font-weight: 500;
    color: rgba(255,255,255,0.75); margin: 0;
  }
  .lg-card-sub a { color: rgba(255,255,255,0.95); font-weight: 700; text-decoration: none; }

  /* Card body */
  .lg-card-body { padding: 28px 32px 32px; }
  @media (max-width: 480px) {
    .lg-card-body { padding: 24px 20px 28px; }
    .lg-card-head { padding: 28px 20px 26px; }
  }

  /* Field */
  .lg-field { margin-bottom: 16px; }
  .lg-field-label {
    display: block;
    font-size: 11px; font-weight: 800;
    letter-spacing: 0.18em; text-transform: uppercase;
    color: #64748b; margin-bottom: 7px;
  }
  .lg-field-wrap { position: relative; }
  .lg-field-icon {
    position: absolute; left: 14px; top: 50%;
    transform: translateY(-50%);
    color: #cbd5e1; pointer-events: none;
    display: flex; align-items: center;
    transition: color 0.2s;
  }
  .lg-field-wrap:focus-within .lg-field-icon { color: #14b8a6; }
  .lg-input {
    width: 100%; padding: 13px 14px 13px 44px;
    background: #f8fafc;
    border: 1.5px solid #e2e8f0;
    border-radius: 14px;
    font-size: 14px; font-weight: 500;
    font-family: 'Inter', sans-serif;
    color: #0f172a;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
  }
  .lg-input:focus {
    border-color: #14b8a6;
    box-shadow: 0 0 0 4px rgba(20,184,166,0.12);
    background: #fff;
  }
  .lg-input.error { border-color: #f87171; box-shadow: 0 0 0 4px rgba(248,113,113,0.1); }
  .lg-error { font-size: 11.5px; color: #ef4444; font-weight: 600; margin-top: 5px; display: block; }

  /* Row */
  .lg-row {
    display: flex; align-items: center;
    justify-content: space-between; flex-wrap: wrap;
    gap: 8px; margin-bottom: 22px;
  }
  .lg-check-wrap { display: flex; align-items: center; gap: 8px; }
  .lg-check {
    width: 16px; height: 16px;
    accent-color: #14b8a6;
    cursor: pointer;
  }
  .lg-check-label { font-size: 13px; font-weight: 600; color: #64748b; cursor: pointer; }
  .lg-forgot {
    font-size: 13px; font-weight: 700;
    color: #14b8a6; text-decoration: none;
    transition: color 0.2s;
  }
  .lg-forgot:hover { color: #0d9488; text-decoration: underline; }

  /* Submit btn */
  .lg-submit {
    width: 100%; padding: 15px;
    background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
    color: #fff; border: none; border-radius: 14px;
    font-size: 14px; font-weight: 900;
    font-family: 'Inter', sans-serif;
    letter-spacing: 0.08em; text-transform: uppercase;
    cursor: pointer;
    box-shadow: 0 8px 28px rgba(20,184,166,0.35);
    transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;
    display: flex; align-items: center; justify-content: center; gap: 8px;
  }
  .lg-submit:hover { transform: translateY(-2px); box-shadow: 0 14px 36px rgba(20,184,166,0.45); }
  .lg-submit:active { transform: scale(0.97); }

  /* Divider */
  .lg-divider {
    display: flex; align-items: center; gap: 12px;
    margin: 22px 0;
  }
  .lg-divider-line { flex: 1; height: 1px; background: #e2e8f0; }
  .lg-divider-text { font-size: 12px; font-weight: 700; color: #94a3b8; }

  /* Register link */
  .lg-signup-row { text-align: center; font-size: 14px; color: #64748b; font-weight: 500; }
  .lg-signup-link { font-weight: 800; color: #14b8a6; text-decoration: none; }
  .lg-signup-link:hover { color: #0d9488; text-decoration: underline; }

  /* Alert */
  .lg-alert {
    display: flex; align-items: center; gap: 10px;
    padding: 12px 16px; border-radius: 12px;
    font-size: 13px; font-weight: 600;
    margin-bottom: 18px;
    animation: lgFadeUp 0.3s ease;
  }
  .lg-alert--error { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
  .lg-alert--success { background: #f0fdfa; color: #0d9488; border: 1px solid #99f6e4; }
`;

function Login() {
  const authState = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [alert, setAlert] = useState(null);
  const [showPass, setShowPass] = useState(false);

  const LoginFormik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: loginSchema,
    onSubmit: (values) => dispatch(loginUser(values)),
  });

  useEffect(() => {
    if (authState.isError) {
      const msg =
        typeof authState.message === "string"
          ? authState.message
          : "Login failed. Please try again.";
      setAlert({ type: "error", msg });
    }
  }, [authState.isError, authState.message]);

  useEffect(() => {
    if (authState.user?.token && !authState.isError) {
      setAlert({ type: "success", msg: "Welcome back! Redirecting…" });
      setTimeout(() => (window.location.href = "/"), 800);
    }
  }, [authState.user]);

  return (
    <>
      <style>{STYLES}</style>
      <div className="lg-page">
        <div className="lg-orb lg-orb--1" />
        <div className="lg-orb lg-orb--2" />

        <div className="lg-center">
          <div className="lg-card">
            {/* Header */}
            <div className="lg-card-head">
              <div className="lg-logo-ring">💊</div>
              <h1 className="lg-card-title">Welcome Back</h1>
              <p className="lg-card-sub">
                Sign in to your <Link to="/">Pulse Addis</Link> pharmacy account
              </p>
            </div>

            {/* Body */}
            <div className="lg-card-body">
              {/* Alert */}
              {alert && (
                <div className={`lg-alert lg-alert--${alert.type}`}>
                  {alert.type === "error" ? "⚠️" : "✅"} {alert.msg}
                </div>
              )}

              <form onSubmit={LoginFormik.handleSubmit} noValidate>
                {/* Email */}
                <div className="lg-field">
                  <label className="lg-field-label" htmlFor="login-email">
                    Email Address
                  </label>
                  <div className="lg-field-wrap">
                    <span className="lg-field-icon">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect width="20" height="16" x="2" y="4" rx="2" />
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                      </svg>
                    </span>
                    <input
                      id="login-email"
                      className={`lg-input${LoginFormik.touched.email && LoginFormik.errors.email ? " error" : ""}`}
                      placeholder="your@email.com"
                      type="email"
                      name="email"
                      value={LoginFormik.values.email}
                      onChange={LoginFormik.handleChange}
                      onBlur={LoginFormik.handleBlur}
                      autoComplete="email"
                    />
                  </div>
                  {LoginFormik.touched.email && LoginFormik.errors.email && (
                    <span className="lg-error">
                      ⚠ {LoginFormik.errors.email}
                    </span>
                  )}
                </div>

                {/* Password */}
                <div className="lg-field">
                  <label className="lg-field-label" htmlFor="login-password">
                    Password
                  </label>
                  <div className="lg-field-wrap">
                    <span className="lg-field-icon">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect
                          width="18"
                          height="11"
                          x="3"
                          y="11"
                          rx="2"
                          ry="2"
                        />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </span>
                    <input
                      id="login-password"
                      className={`lg-input${LoginFormik.touched.password && LoginFormik.errors.password ? " error" : ""}`}
                      placeholder="••••••••"
                      type={showPass ? "text" : "password"}
                      name="password"
                      value={LoginFormik.values.password}
                      onChange={LoginFormik.handleChange}
                      onBlur={LoginFormik.handleBlur}
                      autoComplete="current-password"
                      style={{ paddingRight: "44px" }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      style={{
                        position: "absolute",
                        right: 14,
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#94a3b8",
                        display: "flex",
                        alignItems: "center",
                      }}
                      aria-label="Toggle password visibility"
                    >
                      {showPass ? (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                          <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                          <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                          <line x1="2" y1="2" x2="22" y2="22" />
                        </svg>
                      ) : (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {LoginFormik.touched.password &&
                    LoginFormik.errors.password && (
                      <span className="lg-error">
                        ⚠ {LoginFormik.errors.password}
                      </span>
                    )}
                </div>

                {/* Remember / Forgot */}
                <div className="lg-row">
                  <label className="lg-check-wrap">
                    <input
                      id="remember-me"
                      type="checkbox"
                      className="lg-check"
                    />
                    <span className="lg-check-label">Remember me</span>
                  </label>
                  <Link to="/forgot-password" className="lg-forgot">
                    Forgot password?
                  </Link>
                </div>

                {/* Submit */}
                <button type="submit" className="lg-submit">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                    <polyline points="10 17 15 12 10 7" />
                    <line x1="15" y1="12" x2="3" y2="12" />
                  </svg>
                  Sign In
                </button>

                {/* Divider */}
                <div className="lg-divider">
                  <div className="lg-divider-line" />
                  <span className="lg-divider-text">OR</span>
                  <div className="lg-divider-line" />
                </div>

                {/* Sign up link */}
                <p className="lg-signup-row">
                  New to Pulse Addis?{" "}
                  <Link to="/register" className="lg-signup-link">
                    Create Account →
                  </Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Login;
